export const EFFECTIVE_ACCESS_PERMISSION = {
  PLATFORM_CUSTOMER_MANAGE: "platform.customer.manage",
  PLATFORM_CONFIGURATIONS_MANAGE: "platform.configurations.manage",
  PLATFORM_USER_ACCESS_CONTROL_MANAGE: "platform.userAccessControl.manage",
  ADMIN_USER_MANAGEMENT_MANAGE: "admin.userManagement.manage",
  ADMIN_DATA_MANAGEMENT_MANAGE: "admin.dataManagement.manage",
  APP_HOME_VIEW: "app.home.view",
  APP_DASHBOARD_CONFIGURE: "app.dashboard.configure",
  APP_ALARMS_VIEW: "app.alarms.view",
  APP_ALARMS_OPERATE: "app.alarms.operate",
  APP_ALARMS_CONFIGURE: "app.alarms.configure",
  APP_AUDIT_LOG_VIEW_EXPORT: "app.auditLog.viewExport",
  APP_COLLECTIONS_VIEW: "app.collections.view",
  APP_DISTRIBUTION_VIEW: "app.distribution.view",
  APP_WATER_TREATMENT_VIEW: "app.waterTreatment.view",
  APP_WASTEWATER_TREATMENT_VIEW: "app.wastewaterTreatment.view",
  APP_COMPLIANCE_VIEW: "app.compliance.view",
  APP_II_VIEW: "app.ii.view",
};

export const APPLICATION_RETURN_PERMISSIONS = Object.freeze([
  EFFECTIVE_ACCESS_PERMISSION.APP_HOME_VIEW,
  EFFECTIVE_ACCESS_PERMISSION.APP_DASHBOARD_CONFIGURE,
  EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_VIEW,
  EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_OPERATE,
  EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_CONFIGURE,
  EFFECTIVE_ACCESS_PERMISSION.APP_AUDIT_LOG_VIEW_EXPORT,
  EFFECTIVE_ACCESS_PERMISSION.APP_COLLECTIONS_VIEW,
  EFFECTIVE_ACCESS_PERMISSION.APP_DISTRIBUTION_VIEW,
  EFFECTIVE_ACCESS_PERMISSION.APP_WATER_TREATMENT_VIEW,
  EFFECTIVE_ACCESS_PERMISSION.APP_WASTEWATER_TREATMENT_VIEW,
  EFFECTIVE_ACCESS_PERMISSION.APP_COMPLIANCE_VIEW,
  EFFECTIVE_ACCESS_PERMISSION.APP_II_VIEW,
]);

export const ALL_EFFECTIVE_ACCESS_PERMISSIONS = Object.freeze(
  Object.values(EFFECTIVE_ACCESS_PERMISSION)
);

export const EFFECTIVE_ACCESS_PAGE_KEY = {
  CUSTOMER: "customer",
  CONFIGURATIONS: "configurations",
  USER_ACCESS_CONTROL: "userAccessControl",
  USER_MANAGEMENT: "userManagement",
  DATA_MANAGEMENT: "dataManagement",
  HOME: "home",
  ALARMS: "alarms",
  AUDIT_LOG: "auditLog",
  COLLECTIONS: "collections",
  DISTRIBUTION: "distribution",
  WATER_TREATMENT: "waterTreatment",
  WASTEWATER_TREATMENT: "wastewaterTreatment",
  COMPLIANCE: "compliance",
  II: "ii",
};

export const EFFECTIVE_ACCESS_PAGE_DEFINITIONS = Object.freeze([
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
    pageLabel: "Customer",
    actionsSummary: "View/Create/Edit",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.PLATFORM_CUSTOMER_MANAGE],
    routeValue: "Groups",
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.CONFIGURATIONS,
    pageLabel: "Configurations",
    actionsSummary: "View/Create/Edit",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.PLATFORM_CONFIGURATIONS_MANAGE],
    routeValue: "ThingType",
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL,
    pageLabel: "User Access Control",
    actionsSummary: "View/Create/Edit",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.PLATFORM_USER_ACCESS_CONTROL_MANAGE],
    routeValue: "PlatformUserAccess",
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT,
    pageLabel: "User Management",
    actionsSummary: "View/Create/Edit",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.ADMIN_USER_MANAGEMENT_MANAGE],
    routeValue: "Admin",
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT,
    pageLabel: "Data Management",
    actionsSummary: "View/Create/Edit",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.ADMIN_DATA_MANAGEMENT_MANAGE],
    routeValue: "DataManagement",
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.HOME,
    pageLabel: "Home (Dashboard)",
    permissions: [
      EFFECTIVE_ACCESS_PERMISSION.APP_HOME_VIEW,
      EFFECTIVE_ACCESS_PERMISSION.APP_DASHBOARD_CONFIGURE,
    ],
    actions: Object.freeze([
      {
        actionKey: "view",
        label: "View (includes changing time selector)",
        permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_HOME_VIEW],
      },
      {
        actionKey: "configureDashboards",
        label:
          "Configure Dashboards (Create, Edit, Remove, Save Layout)",
        permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_DASHBOARD_CONFIGURE],
      },
    ]),
    routeValue: "Dashboard",
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.ALARMS,
    pageLabel: "Alarms",
    permissions: [
      EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_VIEW,
      EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_OPERATE,
      EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_CONFIGURE,
    ],
    actions: Object.freeze([
      {
        actionKey: "view",
        label: "View",
        permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_VIEW],
      },
      {
        actionKey: "operate",
        label: "Acknowledge, Snooze, Unsnooze and Comment",
        permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_OPERATE],
      },
      {
        actionKey: "configure",
        label: "Configure (create, edit, delete alarm configurations)",
        permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_ALARMS_CONFIGURE],
      },
    ]),
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.AUDIT_LOG,
    pageLabel: "Audit Log",
    actionsSummary: "View, Filter, Search, Export",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_AUDIT_LOG_VIEW_EXPORT],
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.COLLECTIONS,
    pageLabel: "Collections",
    actionsSummary: "View",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_COLLECTIONS_VIEW],
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.DISTRIBUTION,
    pageLabel: "Distribution",
    actionsSummary: "View",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_DISTRIBUTION_VIEW],
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.WATER_TREATMENT,
    pageLabel: "Water Treatment",
    actionsSummary: "View",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_WATER_TREATMENT_VIEW],
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.WASTEWATER_TREATMENT,
    pageLabel: "Wastewater Treatment",
    actionsSummary: "View",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_WASTEWATER_TREATMENT_VIEW],
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.COMPLIANCE,
    pageLabel: "Compliance",
    actionsSummary: "View",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_COMPLIANCE_VIEW],
  },
  {
    pageKey: EFFECTIVE_ACCESS_PAGE_KEY.II,
    pageLabel: "I&I",
    actionsSummary: "View",
    permissions: [EFFECTIVE_ACCESS_PERMISSION.APP_II_VIEW],
  },
]);
